import { useEffect, useLayoutEffect, useState } from "react";
import { Sparkles, X, ChevronRight, CalendarDays, Users, Save, PartyPopper } from "lucide-react";
import { Button } from "./ui/button";

const SEEN_KEY = "poshan_premium_welcome_seen_v1";

const TOUR_STEPS = [
  {
    testid: "daily-date-btn",
    title: "1 · Pick the date",
    body: "Tap here to choose today or any past day. The menu, rate period and Laddoo policy auto-load.",
    icon: <CalendarDays size={16}/>,
  },
  {
    testid: "daily-cat-primary",
    title: "2 · Enter attendance",
    body: "Use +/– to enter students per category. Costs and rice auto-compute using the official PM POSHAN engine.",
    icon: <Users size={16}/>,
  },
  {
    testid: "daily-save-btn",
    title: "3 · Save the entry",
    body: "Tap Save daily entry to add it to your private register. History, Reports and Bulk PDF unlock instantly.",
    icon: <Save size={16}/>,
  },
];

/**
 * PremiumOnboarding
 * ------------------
 * Shows a one-time celebratory "Premium is now ACTIVE" card + a 3-step tour
 * pointing at real DailyEntry elements via data-testid.
 *
 * Trigger: parent renders <PremiumOnboarding show={boolean} onDismiss={fn} />
 * Persistence: dismissal is recorded in localStorage so it doesn't re-open.
 */
export default function PremiumOnboarding({ show, onDismiss }) {
  const [open, setOpen] = useState(false);
  const [tour, setTour] = useState(0); // 0 = card, 1..3 = tour steps
  const [rect, setRect] = useState(null);

  // Sync open state from parent trigger, but respect localStorage seen flag
  useEffect(() => {
    if (!show) return;
    try {
      if (localStorage.getItem(SEEN_KEY) === "1") { onDismiss?.(); return; }
    } catch (_) { /* SSR-safe */ }
    setOpen(true);
    setTour(0);
  }, [show, onDismiss]);

  // Track the target element's position for the active tour step
  useLayoutEffect(() => {
    if (!open || tour === 0) { setRect(null); return; }
    const step = TOUR_STEPS[tour - 1];
    const target = document.querySelector(`[data-testid="${step.testid}"]`);
    if (!target) { setRect(null); return; }
    target.scrollIntoView({ behavior: "smooth", block: "center" });
    const measure = () => {
      const r = target.getBoundingClientRect();
      setRect({ top: r.top + window.scrollY, left: r.left + window.scrollX, width: r.width, height: r.height });
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, { passive: true });
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure);
    };
  }, [open, tour]);

  const close = () => {
    try { localStorage.setItem(SEEN_KEY, "1"); } catch (_) { /* ignore */ }
    setOpen(false);
    onDismiss?.();
  };

  const startTour = () => setTour(1);
  const nextStep = () => {
    if (tour >= TOUR_STEPS.length) return close();
    setTour((s) => s + 1);
  };

  if (!open) return null;

  // ─── Step 0: Success / Welcome card ────────────────────────────────────
  if (tour === 0) {
    return (
      <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/50 p-4" data-testid="premium-welcome-overlay">
        <div className="relative w-full max-w-md rounded-2xl border-2 border-emerald-300 bg-white p-6 shadow-2xl" data-testid="premium-welcome-card">
          <button onClick={close} aria-label="Close" className="absolute top-3 right-3 h-8 w-8 grid place-items-center rounded-full hover:bg-slate-100" data-testid="premium-welcome-close">
            <X size={16}/>
          </button>
          <div className="flex items-center gap-2">
            <span className="h-10 w-10 rounded-xl bg-emerald-600 text-white grid place-items-center shadow"><PartyPopper size={20}/></span>
            <div>
              <div className="chip chip-green" data-testid="premium-welcome-badge"><Sparkles size={12}/> Premium is now ACTIVE</div>
              <h3 className="mt-1 font-display text-xl font-extrabold text-slate-900">Welcome to Premium 🎉</h3>
            </div>
          </div>
          <p className="mt-3 text-sm text-slate-700">
            Your yearly plan is live. You can now save Daily Entries, view History, run
            Reports and download Bulk PDF — no logout or refresh needed.
          </p>
          <ul className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-700">
            <li className="flex items-center gap-1"><span className="text-emerald-700">✓</span> Save Daily Entries</li>
            <li className="flex items-center gap-1"><span className="text-emerald-700">✓</span> Daily History</li>
            <li className="flex items-center gap-1"><span className="text-emerald-700">✓</span> Weekly / Monthly Reports</li>
            <li className="flex items-center gap-1"><span className="text-emerald-700">✓</span> Bulk PDF</li>
          </ul>
          <div className="mt-5 flex gap-2 justify-end">
            <Button variant="outline" onClick={close} data-testid="premium-welcome-skip">Skip tour</Button>
            <Button onClick={startTour} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="premium-welcome-start">
              Start 3-step tour <ChevronRight size={14}/>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Steps 1..3: Tour tooltip anchored to a real element ───────────────
  const step = TOUR_STEPS[tour - 1];
  // Fallback centered overlay if we can't measure
  if (!rect) {
    return (
      <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/40 p-4" data-testid={`premium-tour-step-${tour}`}>
        <TourCard step={step} tour={tour} total={TOUR_STEPS.length} onNext={nextStep} onSkip={close}/>
      </div>
    );
  }

  // Position the card just below the highlighted element (or above if too low)
  const pageH = document.documentElement.scrollHeight;
  const cardTop = rect.top + rect.height + 12;
  const flipAbove = rect.top + rect.height + 220 > window.scrollY + window.innerHeight;
  const finalTop = flipAbove ? Math.max(rect.top - 200, window.scrollY + 12) : cardTop;

  return (
    <>
      {/* Dimmed backdrop with a punched-out ring around the target */}
      <div className="fixed inset-0 z-[70] bg-slate-950/50" onClick={close} data-testid={`premium-tour-backdrop-${tour}`}/>
      <div
        className="absolute z-[75] rounded-xl ring-4 ring-amber-300 ring-offset-2 ring-offset-emerald-100 pointer-events-none"
        style={{ top: rect.top - 4, left: rect.left - 4, width: rect.width + 8, height: rect.height + 8, maxHeight: pageH }}
        aria-hidden
      />
      <div
        className="absolute z-[80] w-[92vw] max-w-sm"
        style={{ top: finalTop, left: Math.min(Math.max(rect.left, 12), window.innerWidth - 360) }}
        data-testid={`premium-tour-step-${tour}`}
      >
        <TourCard step={step} tour={tour} total={TOUR_STEPS.length} onNext={nextStep} onSkip={close}/>
      </div>
    </>
  );
}

function TourCard({ step, tour, total, onNext, onSkip }) {
  return (
    <div className="rounded-2xl border-2 border-emerald-300 bg-white p-4 shadow-2xl">
      <div className="flex items-center gap-2">
        <span className="h-8 w-8 rounded-lg bg-emerald-700 text-white grid place-items-center">{step.icon}</span>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">Step {tour} of {total}</div>
          <div className="font-display font-extrabold text-slate-900">{step.title}</div>
        </div>
      </div>
      <p className="mt-2 text-sm text-slate-700">{step.body}</p>
      <div className="mt-3 flex items-center justify-between gap-2">
        <button onClick={onSkip} className="text-xs font-semibold text-slate-500 hover:text-slate-800" data-testid="premium-tour-skip">Skip tour</button>
        <Button onClick={onNext} className="bg-emerald-700 hover:bg-emerald-800 text-white h-9" data-testid="premium-tour-next">
          {tour === total ? "Finish" : "Next"} <ChevronRight size={14}/>
        </Button>
      </div>
    </div>
  );
}
